<template>
    <main
        class="w-full h-full min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto"
    >
        <section
            aria-labelledby="primary-heading"
            class="w-full flex min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-6xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Personal Access Tokens</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <div class="sm:flex sm:items-center">
                    <div class="sm:flex-auto">
                        <profile-header-tabs current="personal-access-tokens" />
                    </div>
                </div>

                <div
                    data-testid="pat-directory-section"
                    class="my-4 mx-1 rounded-xl border border-taupe-200 bg-white p-6 sm:p-7"
                >
                    <div class="sm:flex sm:items-center sm:justify-between">
                        <div>
                            <h3
                                class="text-lg font-medium leading-6 text-taupe-800"
                            >
                                Personal Access Tokens
                            </h3>
                            <p class="mt-1 max-w-2xl text-sm text-taupe-500">
                                Tokens you create here authenticate as you —
                                they can do whatever you can currently do, and
                                nothing more.
                            </p>
                            <p
                                v-if="!hasPlanFeature('management_api_access')"
                                class="mt-1 max-w-2xl text-sm text-taupe-500 italic"
                            >
                                Creating new tokens needs a higher plan — no
                                sales call, just upgrade in Billing.
                            </p>
                        </div>
                        <div
                            class="mt-4 sm:mt-0 sm:ml-4 sm:shrink-0 flex items-center gap-3"
                        >
                            <zen-button
                                v-if="hasPlanFeature('management_api_access')"
                                type="button"
                                variant="primary"
                                @click="openCreateModal"
                            >
                                Create token
                            </zen-button>
                        </div>
                    </div>

                    <div class="mt-6">
                        <div v-if="loading" class="text-sm text-taupe-500 py-4">
                            Loading tokens…
                        </div>
                        <div
                            v-else-if="tokens.length === 0"
                            class="rounded-lg border border-dashed border-taupe-300 bg-taupe-50"
                        >
                            <zen-empty-table-message compact>
                                No personal access tokens yet. Create one to get
                                started.
                            </zen-empty-table-message>
                        </div>
                        <div v-else class="overflow-x-auto">
                            <table
                                class="min-w-full divide-y divide-taupe-200"
                                aria-label="Personal access tokens"
                            >
                                <thead>
                                    <tr>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Name
                                        </th>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Created
                                        </th>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Last used
                                        </th>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Expires
                                        </th>
                                        <th
                                            class="py-3 text-left text-xs font-medium text-taupe-500 uppercase tracking-wider"
                                        >
                                            Status
                                        </th>
                                        <th
                                            class="py-3"
                                            aria-label="Actions"
                                        ></th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-taupe-100">
                                    <tr
                                        v-for="token in tokens"
                                        :key="token.ulid"
                                        class="hover:bg-taupe-50"
                                    >
                                        <td
                                            class="py-3 text-sm font-medium text-taupe-800"
                                        >
                                            {{ token.name }}
                                        </td>
                                        <td class="py-3 text-sm text-taupe-500">
                                            {{ formatDate(token.created_at) }}
                                        </td>
                                        <td class="py-3 text-sm text-taupe-500">
                                            {{
                                                token.last_used_at
                                                    ? formatDate(
                                                          token.last_used_at
                                                      )
                                                    : 'Never'
                                            }}
                                        </td>
                                        <td class="py-3 text-sm text-taupe-500">
                                            {{
                                                token.expires_at
                                                    ? formatDate(
                                                          token.expires_at
                                                      )
                                                    : 'Never'
                                            }}
                                        </td>
                                        <td class="py-3">
                                            <span
                                                v-if="isExpired(token)"
                                                class="inline-flex items-center rounded-full bg-taupe-100 px-2.5 py-0.5 text-xs font-medium text-taupe-500"
                                            >
                                                Expired
                                            </span>
                                            <span
                                                v-else
                                                class="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/10"
                                            >
                                                Active
                                            </span>
                                        </td>
                                        <td class="py-3 text-right">
                                            <zen-button
                                                type="button"
                                                variant="link-danger"
                                                @click="confirmRevoke(token)"
                                                :aria-label="`Revoke token: ${token.name}`"
                                            >
                                                Revoke
                                            </zen-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <create-personal-access-token-modal
            v-if="showCreateModal"
            :is-open="showCreateModal"
            :processing="creating"
            :error="createError"
            @close="showCreateModal = false"
            @submit="createToken"
        />

        <personal-access-token-created-modal
            v-if="newToken"
            :is-open="!!newToken"
            :token="newToken"
            @close="dismissNewToken"
        />

        <revoke-own-personal-access-token-modal
            :is-open="!!revokeTarget"
            :token="revokeTarget"
            :processing="revoking"
            @close="revokeTarget = null"
            @confirm="revokeToken"
        />
    </main>
</template>

<script>
import personalAccessTokenService from '@/services/personal-access-token.service'
import { errorMessage, successMessage } from '@/utils/notifications'
import ProfileHeaderTabs from '@/screens/_components/profile/HeaderTabs.vue'
import CreatePersonalAccessTokenModal from '@/screens/_components/profile/modals/CreatePersonalAccessTokenModal.vue'
import PersonalAccessTokenCreatedModal from '@/screens/_components/profile/modals/PersonalAccessTokenCreatedModal.vue'
import RevokeOwnPersonalAccessTokenModal from '@/screens/_components/profile/modals/RevokeOwnPersonalAccessTokenModal.vue'
import { usePlan } from '@/composables/usePlan'
import { removeBy, upsertBy } from '@/utils/list-mutations'

// The raw secret is only shown once, from the create response. It never
// belongs on a row in the list.
function toListItem(token) {
    const item = { ...token }
    delete item.raw_token
    delete item.shown_once
    return item
}

export default {
    name: 'PersonalAccessTokensScreen',
    components: {
        ProfileHeaderTabs,
        CreatePersonalAccessTokenModal,
        PersonalAccessTokenCreatedModal,
        RevokeOwnPersonalAccessTokenModal,
    },
    setup() {
        const { hasPlanFeature } = usePlan()
        return { hasPlanFeature }
    },
    data() {
        return {
            loading: true,
            tokens: [],

            showCreateModal: false,
            creating: false,
            createError: null,
            newToken: null,

            revokeTarget: null,
            revoking: false,
        }
    },
    methods: {
        formatDate(iso) {
            if (!iso) return '—'
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        },
        isExpired(token) {
            if (!token.expires_at) return false
            return new Date(token.expires_at).getTime() < Date.now()
        },
        async loadTokens() {
            this.loading = true
            try {
                const response = await personalAccessTokenService.list()
                this.tokens = response.data ?? []
            } catch {
                errorMessage('Failed to load personal access tokens.')
            } finally {
                this.loading = false
            }
        },
        openCreateModal() {
            this.createError = null
            this.showCreateModal = true
        },
        async createToken(payload) {
            this.creating = true
            this.createError = null
            try {
                const response =
                    await personalAccessTokenService.create(payload)

                this.newToken = response.data?.raw_token ?? null
                this.showCreateModal = false
                this.tokens = upsertBy(
                    this.tokens,
                    toListItem(response.data),
                    'ulid',
                    {
                        position: 'prepend',
                    }
                )
            } catch (error) {
                this.createError =
                    error?.message ??
                    'Failed to create token. Please try again.'
            } finally {
                this.creating = false
            }
        },
        dismissNewToken() {
            this.newToken = null
        },
        confirmRevoke(token) {
            this.revokeTarget = token
        },
        async revokeToken() {
            if (!this.revokeTarget) return
            this.revoking = true
            const ulid = this.revokeTarget.ulid
            try {
                await personalAccessTokenService.revoke(ulid)
                successMessage('Token revoked.')
                this.revokeTarget = null
                this.tokens = removeBy(this.tokens, ulid)
            } catch {
                errorMessage('Failed to revoke token.')
            } finally {
                this.revoking = false
            }
        },
    },
    async mounted() {
        await this.loadTokens()
    },
}
</script>
